import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { useGetMergedDimensionMetricsQuery } from "../../store/apis/dimensionMetricsApi";
import { components } from "../../types/api";
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from "../../utils/formatUtils";

import OrbLoader from "../common/OrbLoader";
type DimensionRow = components["schemas"]["DimensionMetricsRow"];

export interface DimensionMetricsSectionProps {
  /** Merge cuyas subcampanas agregan el desglose. */
  mergedCampaignId?: string;
  currency?: string;
}

/**
 * Desgloses por dispositivo y por audiencia de una campana agrupada.
 *
 * ## Por que existe
 *
 * El desglose por dispositivo llevaba dias guardandose sin que nadie pudiera
 * verlo: `DimensionMetrics` solo la leia el informe de briefing. Es el insight
 * de Connected TV, que es justo lo que se pregunta de una campana de video.
 *
 * ## Las audiencias pueden venir vacias, y es normal
 *
 * Su informe de DV360 todavia no llega a ejecutarse —la cuota se agota antes—
 * asi que la tabla no se pinta si no hay filas, en vez de mostrar una cabecera
 * hueca. Cuando el informe empiece a devolver datos aparecera sola.
 *
 * ## Sobre las cifras
 *
 * Es una foto de totales de vida, no una serie diaria: la ingesta borra y
 * reescribe. Por eso no hay filtro de fechas, igual que en los desgloses de
 * site y creatividad.
 */
const DimensionMetricsSection: React.FC<DimensionMetricsSectionProps> = ({
  mergedCampaignId,
  currency,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data, isLoading } = useGetMergedDimensionMetricsQuery(
    { path: { internalCampaignMergeId: mergedCampaignId || "" } },
    {
      skip: !mergedCampaignId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const devices = data?.devices ?? [];
  const audiences = data?.audiences ?? [];

  // Sin ninguno de los dos no hay nada que contar; mejor no ocupar pantalla.
  if (!isLoading && devices.length === 0 && audiences.length === 0) return null;

  const renderTable = (rows: DimensionRow[], firstColumnLabel: string) => (
    <TableContainer
      component={Paper}
      sx={{ borderRadius: 2, maxHeight: 420, overflow: "auto" }}
    >
      <Table stickyHeader sx={{ minWidth: 650 }}>
        <TableHead sx={{ backgroundColor: theme.palette.grey[200] }}>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold" }}>
              {firstColumnLabel}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {t("campaigns.impressions", { defaultValue: "Impressions" })}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {t("campaigns.clicks", { defaultValue: "Clicks" })}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {t("campaigns.ctr", { defaultValue: "CTR" })}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {t("campaigns.cpm", { defaultValue: "CPM" })}
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>
              {t("campaigns.cost", { defaultValue: "Cost" })}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.dimensionValue}
              hover
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                "&:hover": { backgroundColor: theme.palette.grey[100] },
              }}
            >
              <TableCell>
                <Typography variant="subtitle2">
                  {row.dimensionValue}
                </Typography>
              </TableCell>
              <TableCell>{formatNumber(row.impressions)}</TableCell>
              <TableCell>{formatNumber(row.clicks)}</TableCell>
              {/*
                El backend manda el CTR como ratio 0-1, asi que aqui se
                multiplica. Es la convencion del proyecto y el motivo de que
                otras tablas usen la variante NoMultiply: alli el valor ya
                viene escalado.
              */}
              <TableCell>{formatPercentage(row.ctr)}</TableCell>
              <TableCell>{formatCurrency(row.cpm, currency)}</TableCell>
              <TableCell>{formatCurrency(row.cost, currency)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.grey[200]}`,
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {t("campaigns.dimensionMetrics", {
          defaultValue: "Device & Audience",
        })}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100px",
          }}
        >
          <OrbLoader size="medium" />
        </Box>
      ) : (
        <>
          {devices.length > 0 && (
            <Box sx={{ mb: audiences.length > 0 ? 3 : 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t("campaigns.byDevice", { defaultValue: "By device" })}
              </Typography>
              {renderTable(
                devices,
                t("campaigns.device", { defaultValue: "Device" })
              )}
            </Box>
          )}

          {audiences.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t("campaigns.byAudience", { defaultValue: "By audience" })}
              </Typography>
              {renderTable(
                audiences,
                t("campaigns.audience", { defaultValue: "Audience" })
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default DimensionMetricsSection;
